/**
 * drift — the cinematic "the room takes on the record's colour" runtime.
 *
 * The shell (.bleed ambient layer), the persistent player and stream
 * cards all read `var(--drift)` / `var(--glow)`; setting those two custom
 * properties on <html> makes the whole frame ease to a new accent over
 * `--t`. `--purple` (AI / lineage) is a SEPARATE, FIXED channel and is
 * NEVER written here — the two-channel contract from the mock.
 *
 * Hue source: Radio streams do not yet carry a cover-art dominant colour
 * (see the redesign risk log), so for this pass drift stays inside a
 * cohesive Storm-Blue → indigo family, chosen deterministically per
 * stream so the world subtly shifts between sets without ever colliding
 * with the reserved semantic channels (--cyan focus, --purple lineage,
 * --live, --warn). Swap `driftHueForStream` for a real cover-art / genre
 * hue when one lands — every consumer already reacts for free.
 *
 * ── TEM-1192 ──────────────────────────────────────────────────────────
 * This module used to carry its OWN copy of the colour maths, and it had
 * diverged from the other four in two ways, neither deliberate:
 *
 *   1. `darken(hex, f)` took `f` as the amount to REMOVE (`k = 1 - f`), so
 *      the call site `darken(hex, 0.72)` meant a retained 0.28 where the
 *      contract's retained factor is 0.34.
 *   2. it dropped the per-channel blue bias entirely, so `GLOW_SEED`
 *      `#12245a` carried g=36 where `darken('#3b82f6')` is rgb(18,42,90).
 *
 * Both are now converged onto the shared implementation in ./driftColor.
 * `darken`'s `f` is the RETAINED factor in every copy, and the seeds below
 * are DERIVED from the functions rather than written by hand.
 */

import { applyDrift as applyDriftWithTransition, darken, lighten, currentGround, isLightBackground } from './driftColor';

export { darken, lighten, glowFor, ensureReadable, driftFor, refreshDrift, currentGround, resetDriftState } from './driftColor';

export const DRIFT_SEED = '#3b82f6';

/** The seed glow, derived — not hand-written. Kept as a getter-backed constant
 *  so it still reads as a module constant at every existing call site. */
export const GLOW_SEED = darken(DRIFT_SEED);

/**
 * Cohesive, contract-safe accent family (blue → indigo). Deliberately
 * excludes cyan/teal (focus rings), purple (lineage), emerald (--live)
 * and amber (--warn) so the drift channel stays visually distinct.
 */
const DRIFT_PALETTE = [
  '#3b82f6', // storm blue (seed)
  '#2563eb', // blue-600
  '#4f46e5', // indigo-600
  '#6366f1', // indigo-500
  '#1d4ed8', // blue-700
  '#3554d1', // steel blue
];

/** Small stable string hash (djb2) for deterministic hue selection. */
function hashString(s: string): number {
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Pick a cohesive drift hue for a given stream key (id/name). */
export function driftHueForStream(key?: string | null): string {
  if (!key) return DRIFT_SEED;
  return DRIFT_PALETTE[hashString(key) % DRIFT_PALETTE.length];
}

/**
 * Write `--drift` and its matching `--glow` onto <html> so the whole
 * cinematic frame eases to the new accent. Never touches `--purple`.
 * Radio's `--t` is a CSS concern, so no transition property is written here.
 */
export function applyDrift(hex: string): void {
  applyDriftWithTransition(hex, { transition: false });
}

/** Restore the neutral Storm-Blue seed (on clear / empty state). */
export function resetDrift(): void {
  applyDrift(DRIFT_SEED);
}

/** The seed glow for whichever ground is live — dark darkens, light lightens. */
export function seedGlow(): string {
  return isLightBackground(currentGround()) ? lighten(DRIFT_SEED) : darken(DRIFT_SEED);
}
